/* eslint-disable */
import {
  AliasedExpressions,
  DomainBuilder,
  DomainComponentOpticBuilder,
  InvariantBuilder,
  RecipeFactoryBuilder,
  commonAdminCustomExpressions,
  commonAdminViolationExpressions,
  createConstantExpressionFactory,
  createCustomExpressionFactory,
  createCustomInvariantFactory,
  createMapPublishedChunkedDataHandlerFactory,
  createMapPublishedRevisionDataHandlerFactory,
  createRelationFactory,
} from '@salsita-ai/platform-project-sdk/domain'

import { $Map, $entityReflections, constants } from './admin-enhanced-model.js'
import { Aliases, AnyDomainEntity, RootEntity, aliases, entities, modelVersion } from './admin-model/index.js'
import { FrontendData, PublishedData, SessionData } from './application-types.js'

const implementRelation = createRelationFactory<
  AnyDomainEntity,
  RootEntity,
  $Map,
  PublishedData,
  SessionData,
  FrontendData,
  Aliases
>(aliases)
export const relationExpressions = {
  Admin: {
    $pricingSettings: implementRelation<'Admin', 'PricingSettings'>({
      kind: 'allOfType',
      target: 'PricingSettings',
      cardinality: '0-N',
    }),
    $presets: implementRelation<'Admin', 'Preset'>({
      kind: 'allOfType',
      target: 'Preset',
      cardinality: '0-N',
    }),
    $presetCategories: implementRelation<'Admin', 'PresetCategory'>({
      kind: 'allOfType',
      target: 'PresetCategory',
      cardinality: '0-N',
    }),
    $recipes: implementRelation<'Admin', 'Recipe'>({
      kind: 'allOfType',
      target: 'Recipe',
      cardinality: '0-N',
    }),
    $connectedDataConfigs: implementRelation<'Admin', 'ConnectedDataConfig'>({
      kind: 'allOfType',
      target: 'ConnectedDataConfig',
      cardinality: '0-N',
    }),
    $dataTables: implementRelation<'Admin', 'DataTable'>({
      kind: 'allOfType',
      target: 'DataTable',
      cardinality: '0-N',
    }),
  },
  PricingSettings: {
    $admin: implementRelation<'PricingSettings', 'Admin'>({
      kind: 'allOfType',
      target: 'Admin',
      cardinality: '1-1',
    }),
  },
  PresetCategory: {
    $admin: implementRelation<'PresetCategory', 'Admin'>({
      kind: 'allOfType',
      target: 'Admin',
      cardinality: '1-1',
    }),
    $presets: implementRelation<'PresetCategory', 'Preset'>({
      kind: 'indirect',
      field: 'categoryId',
      target: 'Preset',
      cardinality: '0-N',
    }),
  },
  Preset: {
    $admin: implementRelation<'Preset', 'Admin'>({
      kind: 'allOfType',
      target: 'Admin',
      cardinality: '1-1',
    }),
    $category: implementRelation<'Preset', 'PresetCategory'>({
      kind: 'direct',
      field: 'categoryId',
      target: 'PresetCategory',
      cardinality: '0-1',
    }),
  },
  Recipe: {
    $admin: implementRelation<'Recipe', 'Admin'>({
      kind: 'allOfType',
      target: 'Admin',
      cardinality: '1-1',
    }),
  },
  ConnectedDataConfig: {
    $admin: implementRelation<'ConnectedDataConfig', 'Admin'>({
      kind: 'allOfType',
      target: 'Admin',
      cardinality: '1-1',
    }),
  },
  DataTable: {
    $admin: implementRelation<'DataTable', 'Admin'>({
      kind: 'allOfType',
      target: 'Admin',
      cardinality: '1-1',
    }),
  },
} as const satisfies AliasedExpressions<AnyDomainEntity, RootEntity, PublishedData, SessionData, FrontendData, Aliases>

export const constantExpressions = createConstantExpressionFactory<
  AnyDomainEntity,
  RootEntity,
  PublishedData,
  SessionData,
  FrontendData,
  Aliases
>(
  {} // insert expression metas here, once ready
)(constants)

export const createAdminRecipe = RecipeFactoryBuilder.forAliases<AnyDomainEntity, Aliases>(aliases)
  .forEntities(AnyDomainEntity.reflections)
  .withDefaults({})
  .build()
interface CustomInvariantBlueprints {
  Preset: {
    nameNotEmpty: 'nameCanNotBeEmpty'
  }
}
const staticViolations = {} as const
export const implementCustomAdminInvariants = createCustomInvariantFactory<
  AnyDomainEntity,
  RootEntity,
  $Map,
  PublishedData,
  SessionData,
  FrontendData,
  Aliases,
  CustomInvariantBlueprints
>()(staticViolations)

export const adminInvariantBuilder = new InvariantBuilder<
  AnyDomainEntity,
  RootEntity,
  $Map,
  PublishedData,
  SessionData,
  FrontendData,
  Aliases
>(aliases).withInvariants(implementCustomAdminInvariants(commonAdminViolationExpressions))

export const attachAdminComponent = DomainComponentOpticBuilder.from<
  AnyDomainEntity,
  RootEntity,
  $Map,
  PublishedData,
  SessionData,
  FrontendData,
  Aliases
>(aliases)

export const implementAdminCustomExpressions = createCustomExpressionFactory<
  AnyDomainEntity,
  RootEntity,
  $Map,
  PublishedData,
  SessionData,
  FrontendData,
  Aliases
>(
  {} // TODO pass in meta-data once we have them
)
export const dataExpressions = implementAdminCustomExpressions({})

export const createMapPublishedRevisionDataHandler = createMapPublishedRevisionDataHandlerFactory<
  AnyDomainEntity,
  RootEntity,
  $Map,
  PublishedData
>()

export const createMapPublishedChunkedDataHandler = createMapPublishedChunkedDataHandlerFactory<
  AnyDomainEntity,
  RootEntity,
  $Map
>()

export const adminDomainBuilder = DomainBuilder.from<
  AnyDomainEntity,
  RootEntity,
  $Map,
  PublishedData,
  SessionData,
  FrontendData,
  Aliases
>(
  entities,
  aliases,
  $entityReflections,
  RootEntity.types,
  modelVersion,
  PublishedData,
  SessionData,
  FrontendData,
  undefined,
  true
)
  .withExpressions(constantExpressions)
  .withExpressions(relationExpressions)
  .withExpressions(implementAdminCustomExpressions(commonAdminCustomExpressions))
